import {ChangeDetectionStrategy, ChangeDetectorRef, Component, input, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MøteprotokollRedigerDeltakerKomponent, SkjemaType as DeltakerSkjemaType} from "../møteprotokoll-rediger-deltaker/møteprotokoll-rediger-deltaker";
import {ProtokollførtDeltakerstatus, ProtokollførtMøtedeltakereModell, ProtokollSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormArray, FormGroup} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {MøteprotokollRedigerDeltakerEksternKomponent, SkjemaType as DeltakerEksternSkjemaType, SkjemaVerdier as DeltakerEksternSkjemaVerdier} from "../møteprotokoll-rediger-deltaker-ekstern/møteprotokoll-rediger-deltaker-ekstern";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {distinctUntilChanged, startWith, switchMap} from "rxjs";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erIkkeTomListe, erLikeLister, erTomListe, siste} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {map} from "rxjs/operators";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {leggTilFeilDersom} from "felles-ng2/infrastruktur/angular/reactiveForm";

const debug = initDebugger('MøteprotokollRedigerDeltakereKomponent');


export type SkjemaType = ReturnType<typeof MøteprotokollRedigerDeltakereKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

/**
 * GUI for redigering av protokollførte møtedeltakere
 */
@Component({
    selector: 'moeteprotokoll-rediger-deltakere',
    templateUrl: 'møteprotokoll-rediger-deltakere.html',
    styleUrl: 'møteprotokoll-rediger-deltakere.scss',
    host: {
        'data-testid': 'rediger-deltakere',
        '[class.viser-representasjoner]': 'visRepresentererKolonne()'
    },
    imports: [
        MøteprotokollRedigerDeltakerKomponent,
        TranslateModule,
        MøteprotokollRedigerDeltakerEksternKomponent,
        TipsKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollRedigerDeltakereKomponent {

    static opprettSkjema(deltakere: ProtokollførtMøtedeltakereModell | null = null) {
        const skjema = new FormGroup({
            deltakere: new FormArray<DeltakerSkjemaType>([]),
            eksterneDeltakere: new FormArray<DeltakerEksternSkjemaType>([])
        });
        if (erIkkeNil(deltakere)) MøteprotokollRedigerDeltakereKomponent.populerSkjema(skjema, deltakere);
        return skjema;
    }

    static populerSkjema(skjema: SkjemaType, deltakere: ProtokollførtMøtedeltakereModell) {
        //vi kombinerer deltatte, forfalte og ikkeInnkalte til en liste her for å forenkle oppdateringen av disse gruppene i GUI når deltaker.status endres i skjemaet
        skjema.controls.deltakere.clear();
        [...deltakere.deltatte, ...deltakere.forfalte, ...deltakere.ikkeInnkalte]
          .map(deltaker => MøteprotokollRedigerDeltakerKomponent.opprettSkjema(deltaker))
          .forEach(rad => skjema.controls.deltakere.push(rad));

        deltakere.eksterne
          .map(deltaker => MøteprotokollRedigerDeltakerEksternKomponent.opprettSkjema(deltaker))
          //siden vi legger til deltakerne før siste tomme rad (initielt vil skjema inneholde en tom rad på indeks 0) vil ikke
          //vedEndringIEksterneDeltakere() fører til at det blir lagt til ny tom og unødvendig rad pga insert-kallet (hver insert kall vil trigge vedEndringIEksterneDeltakere())
          .forEach((rad, indeks) => skjema.controls.eksterneDeltakere.insert(indeks, rad));
    }

    static somSpesifikasjon(skjemaverdier: SkjemaVerdier): Pick<ProtokollSpesifikasjon, 'deltakere' | 'eksterneDeltakere'> {
        return {
            deltakere: skjemaverdier.deltakere!.map(deltaker => MøteprotokollRedigerDeltakerKomponent.somSpesifikasjon(deltaker)),
            eksterneDeltakere: skjemaverdier.eksterneDeltakere!
              .filter(deltaker => erIkkeTomDeltakerEksternRad(deltaker))
              .map(deltaker => MøteprotokollRedigerDeltakerEksternKomponent.somSpesifikasjon(deltaker))
        };
    }

    static gjenopprettSkjema(skjema: SkjemaType, skjemaverdier: Pick<ProtokollSpesifikasjon, 'deltakere' | 'eksterneDeltakere'>) {
        skjemaverdier.deltakere.forEach(deltakerVerdier => {
            const eksisterendeDeltaker = skjema.controls.deltakere.controls.find(kandidat => kandidat.value.medlemId == deltakerVerdier.medlemId);
            if (erNil(eksisterendeDeltaker)) {
                debug("gjenopprettSkjema - finner ikke tilsvarende deltaker")
                return;
            }
            eksisterendeDeltaker.patchValue(deltakerVerdier);
        });
        skjemaverdier.eksterneDeltakere.forEach((deltakerVerdier, indeks) => {
            let eksisterendeDeltaker = skjema.controls.eksterneDeltakere.controls.find(kandidat => kandidat.value.id == deltakerVerdier.id);
            if (erNil(eksisterendeDeltaker)) {
                //siden vi legger til deltakeren før siste tomme rad vil ikke
                //vedEndringIEksterneDeltakere() fører til at det blir lagt til ny tom og unødvendig rad pga insert-kallet (hver insert kall vil trigge vedEndringIEksterneDeltakere())
                skjema.controls.eksterneDeltakere.insert(indeks, MøteprotokollRedigerDeltakerEksternKomponent.opprettSkjema())
                eksisterendeDeltaker = skjema.controls.eksterneDeltakere.at(indeks);
            }
            eksisterendeDeltaker.patchValue(deltakerVerdier);
        });
    }

    skjema = input.required<SkjemaType>();

    antallDeltatt = signal(0);
    antallForfall = signal(0);
    antallIkkeInnkalt = signal(0);
    /** Hvorvidt listen over eksterne deltakere skal vises eller ei, er true som standard - uavhengig av om det finnes ikke innkalte med kommentar eller skalSignere=true eller ei */
    viserIkkeInnkalte = signal(true);
    /** Hvorvidt listen over eksterne deltakere skal vises eller ei, blir satt til true dersom det er registrert eksterne deltakere */
    viserEksterne = signal(false);
    /** Hvorvidt informasjon om hvilke aksjeeiere deltaker representerer skal vises, blir satt til true dersom noen av deltakerne har aksjeeierRepresentasjoner */
    visRepresentererKolonne = signal(false);

    statuser = ProtokollførtDeltakerstatus;

    constructor(private cdRef: ChangeDetectorRef) {
        const skjemaSomObservable = toObservable(this.skjema);
        const deltakereSomObservable = skjemaSomObservable
          .pipe(switchMap(skjema => skjema.controls.deltakere.valueChanges.pipe(startWith(skjema.value.deltakere))));

        //FormArray.push (og øvrige metoder på FormArray) trigger ikke rendering - må derfor manuelt signalisere at det har skjedd endringer som skal tegnes ut
        deltakereSomObservable
          .pipe(
            distinctUntilChanged((a, b) => a === b, deltakere => deltakere!.length),
            takeUntilDestroyed())
          .subscribe(() => {
              this.visRepresentererKolonne.set(this.skjema().value.deltakere!.some(deltaker => erIkkeTomListe(deltaker.deltaker?.aksjeeierRepresentasjoner)));
              this.cdRef.markForCheck();
          });

        //oppdater tellere når status på deltaker endres
        deltakereSomObservable
          .pipe(
            map(deltakere => deltakere!.map(deltaker => deltaker.status!)),
            distinctUntilChanged(erLikeLister),
            takeUntilDestroyed())
          .subscribe(statuser => this.oppdaterTellere(statuser));

        //sørg for at det alltid er en tom ekstern-deltaker-rad i listen over eksterne
        skjemaSomObservable
          .pipe(
            switchMap(skjema => skjema.controls.eksterneDeltakere.valueChanges.pipe(startWith(skjema.value.eksterneDeltakere!))),
            takeUntilDestroyed())
          .subscribe(nye => this.vedEndringIEksterneDeltakere(nye));
    }

    endreViserIkkeInnkalte() {
        this.viserIkkeInnkalte.update(vises => !vises);
    }

    endreViserEksterne() {
        this.viserEksterne.update(vises => !vises);
    }

    slettEkstern(indeks: number) {
        this.skjema().controls.eksterneDeltakere.removeAt(indeks);
    }

    //private hjelpemetoder

    private oppdaterTellere(statuser: ProtokollførtDeltakerstatus[]) {
        this.antallDeltatt.set(statuser.filter(status => status == ProtokollførtDeltakerstatus.deltatt).length);
        this.antallForfall.set(statuser.filter(status => status == ProtokollførtDeltakerstatus.forfall).length);
        this.antallIkkeInnkalt.set(statuser.filter(status => status == ProtokollførtDeltakerstatus.ikkeInnkalt).length);
    }

    private vedEndringIEksterneDeltakere(deltakere: DeltakerEksternSkjemaVerdier[]) {
        debug('vedEndringIEksterneDeltakere', deltakere)

        //valider at navnene på deltakerne er unike
        deltakere.forEach((deltaker, indeks) => {
            let navn = deltaker.navn?.toLowerCase();
            const harIkkeUniktNavn = this.skjema().value.deltakere!.some(kandidat => kandidat.deltaker!.medlem.navn.toLowerCase() == navn) ||
              (deltakere.some((ekstern, eksternIndeks) => ekstern.navn?.toLowerCase() == navn && eksternIndeks != indeks));
            leggTilFeilDersom(harIkkeUniktNavn, MøteprotokollRedigerDeltakerEksternKomponent.valideringsfeil.duplikat_navn, this.skjema().controls.eksterneDeltakere.at(indeks).controls.navn)
        })

        //vi viser eksterne deltakere dersom minst en slik er registrert
        if (deltakere.some(deltaker => erIkkeTomDeltakerEksternRad(deltaker)))
            this.viserEksterne.set(true);

        //sørger for at det alltid vises en tom rad til slutt i listen
        this.leggTilNyEksternDeltakerDersomSisteRadIkkeTom(deltakere);

        //sørger for at det ikke vises mer enn en tom rad i slutten av listen
        if (deltakere.slice(-2).filter(rad => erTomDeltakerEksternRad(rad)).length > 1) //sann dersom de to siste radene er tomme
          // fjerner nest siste rad (ikke siste - denne fungere som "trigger" for nye rader, dessuten måtte vi har fjernet required-validering som ligger nest siste om vi fjerner siste...)
            this.skjema().controls.eksterneDeltakere.removeAt(this.skjema().controls.eksterneDeltakere.length - 2);
    }

    private leggTilNyEksternDeltakerDersomSisteRadIkkeTom(deltakere: DeltakerEksternSkjemaVerdier[]) {
        if (erTomListe(deltakere) || erIkkeTomDeltakerEksternRad(siste(deltakere)))
            this.leggTilNyEksternDeltaker();
    }

    private leggTilNyEksternDeltaker(): DeltakerEksternSkjemaType {
        this.skjema().controls.eksterneDeltakere.controls.forEach(rad => MøteprotokollRedigerDeltakerEksternKomponent.settFelterPåkrevd(rad));
        const nyEksternDeltaker = MøteprotokollRedigerDeltakerEksternKomponent.opprettSkjema();
        MøteprotokollRedigerDeltakerEksternKomponent.settFelterPåkrevd(nyEksternDeltaker, false);
        this.skjema().controls.eksterneDeltakere.push(nyEksternDeltaker);
        this.cdRef.markForCheck();
        return nyEksternDeltaker;
    }
}

function erIkkeTomDeltakerEksternRad(rad: DeltakerEksternSkjemaVerdier | null) {
    return !erTomDeltakerEksternRad(rad);
}

function erTomDeltakerEksternRad(rad: DeltakerEksternSkjemaVerdier | null) {
    return erTomEllerNil(rad?.navn) && erTomEllerNil(rad?.rolle);
}
